'use client';

import { useMemo, useState, useEffect, useReducer, useCallback } from 'react';
import { ActionMapType } from 'src/types';

import { postWebUiApi } from '../../api';
import { pathForRoute, WebUiRouteId } from '../../routes';
import { getWebUiStateFromPath } from '../../switch-access';
import { getCookie, removeCookie } from '../../utils/cookie-utils';
import { WebUiGlobalContext } from './global-context';
import { IWebUiCurrentUser, WebUiGlobalContextType } from './types';

// ----------------------------------------------------------------------

enum Types {
  INITIAL = 'INITIAL',
  SET_GLOBAL_LOADING = 'SET_GLOBAL_LOADING',
  SET_CURRENT_USER = 'SET_CURRENT_USER',
}

type Payload = {
  [Types.INITIAL]: {
    globalLoading: boolean;
    currentUser?: IWebUiCurrentUser;
  };
  [Types.SET_GLOBAL_LOADING]: {
    globalLoading: boolean;
  };
  [Types.SET_CURRENT_USER]: {
    currentUser: IWebUiCurrentUser | undefined;
  };
};

type ActionsType = ActionMapType<Payload>[keyof ActionMapType<Payload>];

type PageState = ReturnType<typeof getWebUiStateFromPath>;

const COOKIE_KEY_USER = 'user';

// ----------------------------------------------------------------------

const initialState: WebUiGlobalContextType = {
  globalLoading: true,
  currentUser: undefined,
  setGlobalLoading: () => {},
  currentPage: {
    switchId: null,
    route: 'login',
    accessError: null,
  },
  navigate: () => {},
  login: () => Promise.resolve({ success: false, message: '' }),
  logout: () => {},
};

const reducer = (state: WebUiGlobalContextType, action: ActionsType) => {
  if (action.type === Types.INITIAL) {
    return {
      ...state,
      globalLoading: action.payload.globalLoading,
      currentUser: action.payload.currentUser,
    };
  }
  if (action.type === Types.SET_GLOBAL_LOADING) {
    return {
      ...state,
      globalLoading: action.payload.globalLoading,
    };
  }

  if (action.type === Types.SET_CURRENT_USER) {
    return {
      ...state,
      currentUser: action.payload.currentUser,
    };
  }

  return state;
};

// ----------------------------------------------------------------------

type Props = {
  children: React.ReactNode;
};

export function WebUiGlobalProvider({ children }: Props) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [currentPage, setCurrentPage] = useState<PageState>(() =>
    getWebUiStateFromPath(window.location.pathname),
  );

  const initialize = useCallback(async () => {
    // Skip initialization in test environment to avoid unnecessary side effects
    if (import.meta.env.VITEST) {
      dispatch({
        type: Types.INITIAL,
        payload: {
          globalLoading: false,
          currentUser: undefined,
        },
      });
      return;
    }

    // validate user is expired and password is correct
    const user = getCookie(COOKIE_KEY_USER);
    const userLevel = await postWebUiApi<{ level: number }>({
      service: 'user',
      access: 'get',
      command: 'get level',
      parameters: { user },
    });

    if (user) {
      dispatch({
        type: Types.INITIAL,
        payload: {
          currentUser: {
            userName: user,
            userLevel: userLevel.level,
          },
          globalLoading: false,
        },
      });
    } else {
      dispatch({
        type: Types.INITIAL,
        payload: {
          globalLoading: false,
          currentUser: undefined,
        },
      });
    }
  }, []);

  const setGlobalLoading = useCallback((loading: boolean) => {
    dispatch({
      type: Types.SET_GLOBAL_LOADING,
      payload: { globalLoading: loading },
    });
  }, []);

  const navigate = useCallback((next: WebUiRouteId) => {
    setCurrentPage((prev) => {
      if (!prev.switchId || prev.accessError) return prev;
      window.history.pushState(null, '', pathForRoute(prev.switchId, next));
      return { ...prev, route: next };
    });
  }, []);

  const login = useCallback(
    async ({ email, password }: { email: string; password: string }) => {
      try {
        const response = await postWebUiApi({
          service: 'user',
          access: 'get',
          command: 'login',
          parameters: { user: email, password },
        });
        return response as { success: boolean; message: string };
      } catch (error) {
        return { success: false, message: error.message };
      }
    },
    [],
  );

  const logout = useCallback(async () => {
    try {
      await postWebUiApi({ service: 'user', access: 'get', command: 'logout' });
    } catch {
      // ignore
    }
    removeCookie(COOKIE_KEY_USER);
    dispatch({
      type: Types.SET_CURRENT_USER,
      payload: { currentUser: undefined },
    });
    navigate('login');
  }, [navigate]);

  const setCurrentUser = useCallback((payload: IWebUiCurrentUser) => {
    dispatch({
      type: Types.SET_CURRENT_USER,
      payload: { currentUser: payload },
    });
  }, []);

  useEffect(() => {
    const onPop = () =>
      setCurrentPage(getWebUiStateFromPath(window.location.pathname));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  useEffect(() => {
    if (currentPage.accessError) return;
    if (state.currentUser && currentPage.route === 'login') {
      navigate('dashboard');
      return;
    }
    if (!state.currentUser && currentPage.route !== 'login') navigate('login');
  }, [state.currentUser, currentPage.route, currentPage.accessError, navigate]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // ----------------------------------------------------------------------

  const memoizedValue = useMemo(
    () => ({
      globalLoading: state.globalLoading,
      setGlobalLoading,
      currentUser: state.currentUser,
      setCurrentUser,
      login,
      logout,
      currentPage,
      navigate,
    }),
    [
      state.globalLoading,
      state.currentUser,
      setGlobalLoading,
      setCurrentUser,
      login,
      logout,
      currentPage,
      navigate,
    ],
  );

  return (
    <WebUiGlobalContext.Provider value={memoizedValue}>
      {children}
    </WebUiGlobalContext.Provider>
  );
}
